import type { FC } from "react";

import TelemetryButton from "@/design-system/telemetry-button";
import type { ITelemetryCustom } from "@/lib/telemetry";
import type { ILayerTile } from "@/src/common/LayerTile";
import type { IEntryPointComponent } from "@/src/common/PageTransition";
import { languageSelector, useUserAuthenticationContext } from "@/src/contexts";
import type { ICashbackSteps } from "@/src/homepage/Cashback/CashbackSteps";
import CashbackSteps from "@/src/homepage/Cashback/CashbackSteps";
import FlyersSection, { type IFlyersSection } from "@/src/homepage/Flyers/FlyersSection";
import { useQueryPageCustom } from "@/src/query-client-factory";
import { browserStatics } from "@/src/shared/BrowserStatic";
import MobileStores from "@/src/shared/mobile-stores";
import PageLayout from "@/src/shared/page-layout";
import { HomeEventType, UserLanguageType } from "@Components/Modules/Helpers";
import type { IDiscountTileV3 } from "@Components/discount-tile/discount-tile-v3";
import { allStoresCategoriesPanelId, allStoresPanelId } from "@Constants/PortalConstants";
import { CashbackDetails } from "@Routes/CashbackLinks";
import { FlyersPage } from "@Routes/FlyersLinks";
import { OffersIndex } from "@Routes/OffersLinks";
import { ShoppingMalls } from "@Routes/ShoppingMallLinks";
import type { QueryMdMax, QueryXsMax } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

import RoundList, { type IRoundList } from "../common/RoundList";
import PanelSection from "../shared/panel-section";
import AppFeatureSection, { type IAppFeatureSection } from "./AppFeature/AppFeatureSection";
import BlogSection from "./Blog/BlogSection";
import DiscountsSection from "./Discounts/DiscountsSection";
import HomepageResources from "./HomepageResources";
import HomepageResourcesEn from "./HomepageResources.En";
import ShoppingMallsSection, { type IShoppingMallsSection } from "./ShoppingMalls/ShoppingMallsSection";

export interface IHomepage extends IEntryPointComponent {
    flyersSection: IFlyersSection;
    discountsSection?: IDiscountTileV3[];
    appFeatureSection: IAppFeatureSection;
    cashbackSection: ICashbackSteps;
    shoppingMallsSection: IShoppingMallsSection;
    blogSection: ILayerTile[];
    roundTileList: IRoundList;
}

const viewTelemetry = (evName: string): ITelemetryCustom => ({
    eventName: `homepg-${evName}`,
    actionType: "View",
});

export const Homepage: FC = () => {
    const resources = languageSelector(HomepageResources, HomepageResourcesEn);
    const { isAuthenticated } = useUserAuthenticationContext();
    const { data } = useQueryPageCustom<IHomepage>();

    if (!data) return null;

    const { component: props } = data;

    return (
        <PageLayout layoutAdUnitPath="stonaglowna" slugTarget="/homepage">
            {props.roundTileList && (
                <PanelSection
                    id="cb-stores"
                    styleX={styles.panels}
                    telemetryCustom={viewTelemetry(HomeEventType.CashbackBrandsView)}
                    initialInView
                >
                    <RoundList
                        {...props.roundTileList}
                        title={resources.cashbackSectionBrandsTitle}
                        moreLink={{
                            as: "a",
                            size: "None",
                            variant: "transparent",
                            children: resources.cashbackSectionMoreButton,
                            href: `${CashbackDetails}#${isAuthenticated ? allStoresPanelId : allStoresCategoriesPanelId}`,
                            eventName: "homepg-cshbckBrndsMore",
                            "aria-label": resources.accessibility.cashbackAll,
                        }}
                        carouselTelemetryCustom={{
                            eventName: "homepg-cshbckBrndsSwipe",
                            actionType: "Swipe",
                        }}
                    />
                </PanelSection>
            )}
            {props.cashbackSection && (
                <PanelSection
                    styleX={styles.panels}
                    title={resources.cashbackSectionTitle}
                    subTitle={resources.cashbackSectionSubTitle}
                    redirectButton={
                        <TelemetryButton
                            as="a"
                            size="M"
                            variant="magentaRound"
                            href={CashbackDetails}
                            eventName="homepg-cshbckIntroBtnClick"
                            data-testid="hmpg-cb-button"
                            aria-label={resources.accessibility.yourCashback}
                        >
                            {resources.cashbackSectionButton}
                        </TelemetryButton>
                    }
                    initialInView
                >
                    <CashbackSteps {...props.cashbackSection} />
                </PanelSection>
            )}
            {props.flyersSection && (
                <PanelSection
                    id="latest-flyers"
                    styleX={styles.panels}
                    title={resources.flyersSectionTitle}
                    redirectButton={
                        <TelemetryButton
                            as="a"
                            size="M"
                            variant="magentaRound"
                            href={FlyersPage}
                            eventName="homepg-flyersBtnMore"
                            aria-label={resources.accessibility.flyersAll}
                        >
                            {resources.flyersSectionButton}
                        </TelemetryButton>
                    }
                    description={resources.flyersSectionSubtitle}
                    telemetryCustom={viewTelemetry(HomeEventType.FlyersView)}
                    compAfterButton={
                        props.flyersSection.roundTitleList && (
                            <RoundList
                                {...props.flyersSection.roundTitleList}
                                id="popular-flyers"
                                title={resources.flyersSectionBrandList}
                                carouselTelemetryCustom={viewTelemetry(HomeEventType.FlyerBrandsView)}
                                moreLink={{
                                    as: "a",
                                    size: "None",
                                    variant: "transparent",
                                    children: resources.flyersSectionBrandsMoreButton,
                                    href: FlyersPage,
                                    eventName: "homepg-flyerBrandMore",
                                    "aria-label": resources.accessibility.flyersAll,
                                }}
                            />
                        )
                    }
                    initialInView
                >
                    <FlyersSection {...props.flyersSection} />
                </PanelSection>
            )}
            {props.discountsSection && (
                <PanelSection
                    id="discounts-&-deals"
                    styleX={styles.panels}
                    title={resources.discountsSectionTitle}
                    description={resources.discountsSectionSubtitle}
                    redirectButton={
                        <TelemetryButton
                            as="a"
                            size="M"
                            variant="magentaRound"
                            href={OffersIndex}
                            eventName="homepg-discountBtnMore"
                            aria-label={resources.accessibility.discountsAll}
                        >
                            {resources.discountsSectionButton}
                        </TelemetryButton>
                    }
                    telemetryCustom={viewTelemetry(HomeEventType.DiscountsView)}
                    initialInView
                >
                    <DiscountsSection initialData={props.discountsSection} />
                </PanelSection>
            )}

            {browserStatics.language !== UserLanguageType.English && props.blogSection && (
                <PanelSection
                    id="blog"
                    styleX={styles.panels}
                    title={resources.blogSectionTitle}
                    telemetryCustom={viewTelemetry("LatestContentView")}
                    initialInView
                >
                    <BlogSection initialData={props.blogSection} />
                </PanelSection>
            )}

            {props.shoppingMallsSection && (
                <PanelSection
                    id="shopping-malls"
                    styleX={styles.panels}
                    title={resources.shoppingMallsSection.title}
                    redirectButton={
                        <TelemetryButton
                            as="a"
                            size="M"
                            variant="magentaRound"
                            href={ShoppingMalls}
                            aria-label={resources.accessibility.shoppingMallAll}
                        >
                            {resources.shoppingMallsSection.buttonText}
                        </TelemetryButton>
                    }
                    telemetryCustom={viewTelemetry(HomeEventType.ShoppingMallsView)}
                    initialInView
                >
                    <ShoppingMallsSection {...props.shoppingMallsSection} />
                </PanelSection>
            )}

            {props.appFeatureSection.initialData?.appFeatureList?.length ||
            props.appFeatureSection.initialData?.appFeatureList ? (
                <PanelSection
                    id="app-benefits"
                    styleX={[styles.panels, styles.lastPanel]}
                    title={resources.appSectionOffersTitle}
                    telemetryCustom={viewTelemetry("appIntroduceView")}
                    initialInView
                >
                    <div {...stylex.props(styles.downloadBanner)}>
                        <MobileStores referrer={`goodie_app_section_${resources.appSectionOffersTitle}`} />
                    </div>
                    <AppFeatureSection {...props.appFeatureSection} />
                </PanelSection>
            ) : null}
        </PageLayout>
    );
};
const mdMax: QueryMdMax = "@media (max-width: 1239px)";
const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    panels: {
        margin: {
            default: "100px 0",
            [mdMax]: "80px 0",
            [xsMax]: "60px 0",
            ":first-of-type": "30px 0",
        },
    },

    panelNavButton: {
        marginBottom: 5,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 40,
    },

    downloadBanner: {
        textAlign: "center",
        height: 40,
        marginBottom: 40,
    },

    lastPanel: {
        marginBottom: 0,
    },
});
